import React, { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  Mail,
  Shield,
  Globe,
  UserPlus,
  Clock,
  ArrowLeft,
  CheckCircle2,
} from "lucide-react";

import {
  AuthLayout,
  InfoRow,
  RoleBadge,
  PasswordField,
  PrimaryButton,
  StatusAlert,
} from "@/components/auth";
import { useAuth } from "@/contexts/AuthContext";
import { withAppBase } from "@/lib/appPaths";

const INVITE_STATES = {
  VALID: "valid",
  INVALID: "invalid",
  EXPIRED: "expired",
  ALREADY_USED: "already_used",
  SUCCESS: "success",
} as const;

export default function InviteAccept() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { acceptInvite } = useAuth();

  const [currentState, setCurrentState] = useState<
    (typeof INVITE_STATES)[keyof typeof INVITE_STATES]
  >(INVITE_STATES.VALID);
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [passwordError, setPasswordError] = useState("");
  const [confirmError, setConfirmError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const inviteToken = searchParams.get("token") || "";

  const inviteData = {
    email: "admin@company.com",
    role: "SUPPORT",
    accessScope: "Global Admin Access",
    invitedBy: "ravi@smartstore.com",
    expiresIn: "48 hours",
  };

  const passwordRequirements = [
    {
      key: "length",
      label: "Minimum 16 characters",
      met: password.length >= 16,
    },
    {
      key: "uppercase",
      label: "At least one uppercase letter",
      met: /[A-Z]/.test(password),
    },
    {
      key: "lowercase",
      label: "At least one lowercase letter",
      met: /[a-z]/.test(password),
    },
    {
      key: "number",
      label: "At least one number",
      met: /[0-9]/.test(password),
    },
    {
      key: "special",
      label: "At least one special character (!@#$%^&*)",
      met: /[!@#$%^&*]/.test(password),
    },
  ];

  const allRequirementsMet = passwordRequirements.every((req) => req.met);
  const passwordsMatch =
    password === confirmPassword && confirmPassword.length > 0;
  const isFormValid = allRequirementsMet && passwordsMatch;

  const handleActivate = async () => {
    if (!isFormValid) return;
    setIsSubmitting(true);
    const res = await acceptInvite({ token: inviteToken, password });
    setIsSubmitting(false);
    if (res.ok) {
      setCurrentState(INVITE_STATES.SUCCESS);
      return;
    }
    const reason = ("reason" in res ? res.reason : "UNKNOWN") as
      | "INVALID"
      | "EXPIRED"
      | "ALREADY_USED"
      | "UNKNOWN";
    if (reason === "INVALID") setCurrentState(INVITE_STATES.INVALID);
    else if (reason === "EXPIRED") setCurrentState(INVITE_STATES.EXPIRED);
    else if (reason === "ALREADY_USED")
      setCurrentState(INVITE_STATES.ALREADY_USED);
    else setCurrentState(INVITE_STATES.INVALID);
  };

  if (currentState === INVITE_STATES.INVALID) {
    return (
      <AuthLayout>
        <div data-testid="invalid-invite-state" className="text-center py-6">
          <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-rose-100 dark:bg-rose-950 flex items-center justify-center">
            <Shield className="w-8 h-8 text-rose-500 dark:text-rose-400" />
          </div>
          <h2 className="text-xl font-bold font-heading text-slate-900 dark:text-slate-100 mb-2">
            Invalid Invitation
          </h2>
          <p className="text-sm text-slate-500 dark:text-slate-400 mb-6">
            This invitation link is invalid or malformed.
            <br />
            Please check the link or contact your administrator.
          </p>
          <PrimaryButton
            data-testid="back-to-signin-btn"
            variant="secondary"
            onClick={() => navigate("/login")}
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Sign In
          </PrimaryButton>
        </div>
      </AuthLayout>
    );
  }

  if (currentState === INVITE_STATES.EXPIRED) {
    return (
      <AuthLayout>
        <div data-testid="expired-invite-state" className="text-center py-6">
          <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-amber-100 dark:bg-amber-950 flex items-center justify-center">
            <Clock className="w-8 h-8 text-amber-500 dark:text-amber-400" />
          </div>
          <h2 className="text-xl font-bold font-heading text-slate-900 dark:text-slate-100 mb-2">
            Invitation Expired
          </h2>
          <p className="text-sm text-slate-500 dark:text-slate-400 mb-6">
            This invitation has expired and is no longer valid.
            <br />
            Please contact your administrator for a new invitation.
          </p>
          <PrimaryButton
            data-testid="contact-support-btn"
            onClick={() =>
              (window.location.href = "mailto:support@smartstore.com")
            }
          >
            Contact Support
          </PrimaryButton>
        </div>
      </AuthLayout>
    );
  }

  if (currentState === INVITE_STATES.ALREADY_USED) {
    return (
      <AuthLayout>
        <div data-testid="already-used-state" className="text-center py-6">
          <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-blue-100 dark:bg-blue-950 flex items-center justify-center">
            <CheckCircle2 className="w-8 h-8 text-blue-500 dark:text-blue-400" />
          </div>
          <h2 className="text-xl font-bold font-heading text-slate-900 dark:text-slate-100 mb-2">
            Invitation Already Accepted
          </h2>
          <p className="text-sm text-slate-500 dark:text-slate-400 mb-6">
            This account has already been activated.
            <br />
            You can sign in with your credentials.
          </p>
          <PrimaryButton
            data-testid="go-to-signin-btn"
            onClick={() => navigate("/login")}
          >
            Go to Sign In
          </PrimaryButton>
        </div>
      </AuthLayout>
    );
  }

  if (currentState === INVITE_STATES.SUCCESS) {
    return (
      <AuthLayout>
        <div data-testid="success-state" className="text-center py-6">
          <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-emerald-100 dark:bg-emerald-950 flex items-center justify-center animate-fade-in">
            <CheckCircle2 className="w-8 h-8 text-emerald-500 dark:text-emerald-400" />
          </div>
          <h2 className="text-xl font-bold font-heading text-slate-900 dark:text-slate-100 mb-2">
            Account Activated
          </h2>
          <p className="text-sm text-slate-500 dark:text-slate-400 mb-6">
            Your admin account has been successfully activated.
            <br />
            You can now sign in to access the console.
          </p>
          <StatusAlert
            variant="success"
            title="Welcome to SmartStore Admin"
            message="Your account is ready. Sign in to get started with managing the platform."
            className="mb-6 text-left"
          />
          <PrimaryButton
            data-testid="go-to-signin-success-btn"
            onClick={() => navigate("/login")}
          >
            Go to Sign In
          </PrimaryButton>
        </div>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout
      footer={
        <div className="text-center space-y-2">
          <a
            href={withAppBase("/login")}
            data-testid="already-have-access-link"
            className="text-sm text-indigo-600 dark:text-indigo-400 hover:text-indigo-700 dark:hover:text-indigo-300 transition-colors"
          >
            Already have access? Sign in
          </a>
          <p>
            <a
              href="mailto:support@smartstore.com"
              data-testid="invitation-expired-link"
              className="text-sm text-slate-400 dark:text-slate-500 hover:text-slate-600 dark:hover:text-slate-400 transition-colors"
            >
              Invitation expired? Contact support
            </a>
          </p>
        </div>
      }
    >
      <div data-testid="invite-accept-header" className="mb-6">
        <h2 className="text-xl font-bold font-heading text-slate-900 dark:text-slate-100 mb-2">
          Accept Invitation
        </h2>
        <p className="text-sm text-slate-500 dark:text-slate-400">
          You've been invited to access the SmartStore Admin Console. Please set
          your password to activate your account.
        </p>
      </div>

      <div
        data-testid="invitation-details"
        className="mb-6 p-4 rounded-lg bg-slate-50 dark:bg-slate-800/50 border border-slate-100 dark:border-slate-800"
      >
        <h3 className="text-xs font-semibold uppercase tracking-wider text-slate-400 dark:text-slate-500 mb-3">
          Invitation Details
        </h3>
        <div className="space-y-0">
          <InfoRow label="Email" value={inviteData.email} icon={Mail} />
          <InfoRow
            label="Assigned Role"
            icon={Shield}
            badge={<RoleBadge role={inviteData.role} />}
          />
          <InfoRow
            label="Access Scope"
            value={inviteData.accessScope}
            icon={Globe}
          />
          <InfoRow
            label="Invited By"
            value={inviteData.invitedBy}
            icon={UserPlus}
          />
          <InfoRow
            label="Invitation Expiry"
            value={`Expires in ${inviteData.expiresIn}`}
            icon={Clock}
            muted
          />
        </div>
      </div>

      <form
        data-testid="password-setup-form"
        onSubmit={(e) => {
          e.preventDefault();
          handleActivate();
        }}
        className="space-y-4"
      >
        <PasswordField
          id="new-password"
          label="New Password"
          value={password}
          onChange={(e) => {
            setPassword(e.target.value);
            setPasswordError("");
          }}
          placeholder="Create a secure password"
          error={passwordError}
          showRequirements
          requirements={passwordRequirements}
        />

        <PasswordField
          id="confirm-password"
          label="Confirm Password"
          value={confirmPassword}
          onChange={(e) => {
            setConfirmPassword(e.target.value);
            setConfirmError("");
          }}
          placeholder="Re-enter your password"
          error={
            confirmPassword.length > 0 && !passwordsMatch
              ? "Passwords do not match"
              : confirmError
          }
        />

        <div className="pt-2">
          <PrimaryButton
            data-testid="activate-account-btn"
            type="submit"
            disabled={!isFormValid}
            onClick={handleActivate}
            loading={isSubmitting}
          >
            Activate Account
          </PrimaryButton>
        </div>

        {!isFormValid &&
          (password.length > 0 || confirmPassword.length > 0) && (
            <p className="text-xs text-center text-slate-400 dark:text-slate-500">
              Complete all password requirements to activate your account
            </p>
          )}
      </form>
    </AuthLayout>
  );
}
